/* eslint-disable @tanstack/query/no-unstable-deps, react-hooks/preserve-manual-memoization, react-refresh/only-export-components, react/display-name -- pre-existing legacy issues; disabled to land a mechanical import-path move without a behavior-changing refactor */
import { useCallback, useState, useMemo, memo } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import useUrlState from '@ahooksjs/use-url-state';
import { Flex } from '@chakra-ui/react';
import { useShowToast } from '@/hooks';
import { ModalWithForm } from '@/components/ats/modal/ModalWithForm';
import { KText } from '@/ui-library/components';
import { GroupRequisitionCodes } from '@/components/requisitions/PartOfGroupTag';
import { API_SERVICE, API_KEYS } from '../api';
import { getRequisitionErrorMessage } from '../utils/requisitionErrorHandler';
import {
  getCloseRequisitionFormConfig,
  getArchiveRequisitionFormConfig,
} from '../config';
import type { Requisition } from '../types';
import type { RequisitionActionType } from '../constants/requisitionActions';

const getGroupCodeFormat = (code?: string): string => {
  if (!code) return '';
  const parts = code.split('-');
  if (parts.length > 1) {
    parts[parts.length - 1] = 'X';
    return parts.join('-');
  }
  return code;
};

interface ModalConfig {
  headerText: string | ((code: string) => string);
  subHeaderText?: ((code: string) => string) | (() => string);
  primaryCtaText: string;
  formConfig?: () => any[];
  initialValues?: Record<string, any>;
  withForm?: boolean;
}

const MODAL_CONFIGS: Record<string, ModalConfig> = {
  close: {
    headerText: 'Requisition close reason',
    primaryCtaText: 'Close requisition',
    formConfig: getCloseRequisitionFormConfig,
    initialValues: { rejection_reason: '', rejection_reason_description: '' },
    withForm: true,
  },
  close_single: {
    headerText: (code) => `Requisition ${code} close reason`,
    subHeaderText: (code) =>
      `Marking ${code} alone as closed will remove it from the group and assign a new requisition ID to it`,
    primaryCtaText: 'Close requisition',
    formConfig: getCloseRequisitionFormConfig,
    initialValues: { rejection_reason: '', rejection_reason_description: '' },
    withForm: true,
  },
  archive: {
    headerText: 'Are you sure?',
    subHeaderText: (code) =>
      `Archiving requisition ${code} is permanent and cannot be undone`,
    primaryCtaText: 'Yes, archive',
    formConfig: getArchiveRequisitionFormConfig,
    initialValues: { archiveInput: '' },
    withForm: true,
  },
  make_confidential: {
    headerText: 'Make confidential?',
    subHeaderText: (code) =>
      `Marking ${code} as confidential will hide it from anyone outside the hiring team`,
    primaryCtaText: 'Yes, make confidential',
    withForm: false,
  },
  make_public: {
    headerText: 'Make public?',
    subHeaderText: (code) =>
      `Making ${code} public will make it visible to everyone in your organization`,
    primaryCtaText: 'Yes, make public',
    withForm: false,
  },
  open: {
    headerText: 'Open requisition?',
    subHeaderText: (code) =>
      `Opening ${code} will make it active and ready for candidate applications`,
    primaryCtaText: 'Yes, open requisition',
    withForm: false,
  },
  reopen: {
    headerText: 'Reopen requisition',
    primaryCtaText: 'Reopen',
    subHeaderText: (code) =>
      `Reopening ${code} will open the requisition in draft mode`,
    withForm: false,
  },
  clone: {
    headerText: 'Clone requisition?',
    subHeaderText: (code) =>
      `A draft copy of ${code} will be created. You can edit it before publishing.`,
    primaryCtaText: 'Yes, clone',
    withForm: false,
  },
  // Group action configs
  make_confidential_group: {
    headerText: 'Make confidential?',
    subHeaderText: () =>
      'This will make all requisitions in the group confidential and hide them from anyone outside the hiring team. Are you sure?',
    primaryCtaText: 'Confirm',
    withForm: false,
  },
  make_public_group: {
    headerText: 'Make un-confidential?',
    subHeaderText: () =>
      'This will make the requisition group un-confidential and visible to users without confidential access. Are you sure?',
    primaryCtaText: 'Confirm',
    withForm: false,
  },
  open_group: {
    headerText: 'Open requisition group',
    subHeaderText: () =>
      'Opening this requisition group will make all the requisitions in the group active and ready for usage',
    primaryCtaText: 'Open requisitions',
    withForm: false,
  },
  close_group: {
    headerText: 'Requisition group close reason',
    primaryCtaText: 'Close requisitions',
    formConfig: getCloseRequisitionFormConfig,
    initialValues: { rejection_reason: '', rejection_reason_description: '' },
    withForm: true,
  },
  reopen_group: {
    headerText: 'Reopen requisition group',
    subHeaderText: () =>
      'Reopening this requisition group will open the group in draft mode',
    primaryCtaText: 'Reopen requisitions',
    withForm: false,
  },
  archive_group: {
    headerText: 'Archive requisition group',
    subHeaderText: () =>
      'Archiving this requisition group is permanent and cannot be undone.',
    primaryCtaText: 'Archive requisitions',
    formConfig: getArchiveRequisitionFormConfig,
    initialValues: { archiveInput: '' },
    withForm: true,
  },
};

interface GenericModalProps {
  action: RequisitionActionType;
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (values?: any) => void;
  isLoading: boolean;
  requisitionCode?: string;
  applyToGroup?: boolean;
  allGroupRequisitions?: Requisition[];
}

const GenericRequisitionModal = memo<GenericModalProps>(
  ({
    action,
    isOpen,
    onClose,
    onSubmit,
    isLoading,
    requisitionCode,
    applyToGroup,
    allGroupRequisitions = [],
  }) => {
    // Select the appropriate config based on applyToGroup flag
    const configKey =
      applyToGroup &&
      [
        'open',
        'close',
        'reopen',
        'archive',
        'make_confidential',
        'make_public',
      ].includes(action)
        ? `${action}_group`
        : action;

    // Check if group has filled or associated requisitions (for reopen_group)
    const hasFilledOrAssociatedReqs = useMemo(() => {
      if (configKey !== 'reopen_group' || allGroupRequisitions.length === 0)
        return false;
      return allGroupRequisitions.some(
        (req) => req.status === 'filled' || req.requisition_association?.id
      );
    }, [configKey, allGroupRequisitions]);

    const config = MODAL_CONFIGS[configKey];

    // Early return moved after hooks to satisfy rules-of-hooks
    const formConfig = useMemo(
      () => (config?.formConfig ? config.formConfig() : []),
      [config]
    );

    // For group actions with form, use AdditionalBlock to show content above the form
    const shouldUseAdditionalBlock =
      (configKey === 'close_group' ||
        configKey === 'archive_group' ||
        configKey === 'close_single') &&
      config.withForm;

    const additionalBlock = useMemo(() => {
      if (!shouldUseAdditionalBlock) return null;

      // For close_single, show the description text
      if (
        configKey === 'close_single' &&
        config.subHeaderText &&
        requisitionCode
      ) {
        const subHeaderTextContent = (
          config.subHeaderText as (code: string) => string
        )(requisitionCode);

        return (
          <Flex flexDir="column" gap="12px" p="8px 24px">
            <KText>{subHeaderTextContent}</KText>
          </Flex>
        );
      }

      // For group actions, show GroupRequisitionCodes
      if (allGroupRequisitions.length === 0) return null;

      // For open/reopen actions, filter out associated open reqs and filled reqs
      let filteredGroupReqs = allGroupRequisitions;
      if (action === 'open' || action === 'reopen') {
        filteredGroupReqs = allGroupRequisitions.filter(
          (req) => req.status !== 'open' && req.status !== 'filled'
        );
      }

      return (
        <Flex flexDir="column" gap="12px" p="16px">
          <GroupRequisitionCodes
            requisitions={filteredGroupReqs}
            codeStyles={{ maxHeight: '200px', overflowY: 'auto' }}
          />
        </Flex>
      );
    }, [
      shouldUseAdditionalBlock,
      allGroupRequisitions,
      configKey,
      config,
      requisitionCode,
      action,
    ]);

    const modalChildren = useMemo(() => {
      // Skip children if using AdditionalBlock
      if (shouldUseAdditionalBlock) return undefined;

      if (!config.subHeaderText) return undefined;

      let subHeaderTextContent: string;

      // For reopen_group with filled/associated reqs, use custom message
      if (configKey === 'reopen_group' && hasFilledOrAssociatedReqs) {
        subHeaderTextContent = `${getGroupCodeFormat(
          requisitionCode
        )} includes requisitions that are filled or associated with offers. If you reopen this group, those requisitions will be removed from the group and assigned new requisition IDs. Do you want to continue?`;
      } else if (requisitionCode) {
        subHeaderTextContent = (
          config.subHeaderText as (code: string) => string
        )(requisitionCode);
      } else {
        subHeaderTextContent = (config.subHeaderText as () => string)();
      }

      // For group actions, include GroupRequisitionCodes
      // BUT skip GroupRequisitionCodes for reopen_group with filled/associated reqs
      if (
        applyToGroup &&
        allGroupRequisitions.length > 0 &&
        !(configKey === 'reopen_group' && hasFilledOrAssociatedReqs)
      ) {
        // For open/reopen actions, filter out associated open reqs and filled reqs
        let filteredGroupReqs = allGroupRequisitions;
        if (action === 'open' || action === 'reopen') {
          filteredGroupReqs = allGroupRequisitions.filter(
            (req) => req.status !== 'open' && req.status !== 'filled'
          );
        }

        return (
          <Flex flexDir="column" gap="12px">
            <GroupRequisitionCodes
              requisitions={filteredGroupReqs}
              codeStyles={{ maxHeight: '200px', overflowY: 'auto' }}
            />
            <KText>{subHeaderTextContent}</KText>
          </Flex>
        );
      }

      return (
        <Flex>
          <KText>{subHeaderTextContent}</KText>
        </Flex>
      );
    }, [
      config,
      requisitionCode,
      applyToGroup,
      allGroupRequisitions,
      shouldUseAdditionalBlock,
      action,
      configKey,
      hasFilledOrAssociatedReqs,
    ]);

    if (!config) return null;

    // For reopen_group with filled/associated reqs, override the header text and button text
    let headerText =
      typeof config.headerText === 'function'
        ? config.headerText(requisitionCode || '')
        : config.headerText;

    let primaryCtaText = config.primaryCtaText;

    if (configKey === 'reopen_group' && hasFilledOrAssociatedReqs) {
      headerText = `Reopen ${getGroupCodeFormat(requisitionCode)}?`;
      primaryCtaText = 'Confirm';
    }

    return (
      <ModalWithForm
        isOpen={isOpen}
        onClose={onClose}
        headerText={headerText}
        primaryCtaText={primaryCtaText}
        isCtaLoading={isLoading}
        handleSubmit={onSubmit}
        formConfig={formConfig}
        initialValues={config.initialValues}
        modalHeaderProps={config.withForm ? { pb: '0' } : {}}
        modalFooterProps={config.withForm ? { pt: '8px' } : {}}
        formContainerStyles={{ margin: '0px' }}
        modalContentProps={{ maxW: '500px' }}
        withForm={config.withForm}
        showAdditionalBlock={shouldUseAdditionalBlock}
        AdditionalBlock={additionalBlock}
      >
        {modalChildren}
      </ModalWithForm>
    );
  }
);

interface MutationMessages {
  success: string;
  error: string;
}

const MUTATION_MESSAGES: Record<string, MutationMessages> = {
  close: {
    success: 'Requisition closed successfully',
    error: 'Failed to close requisition',
  },
  close_single: {
    success: 'Requisition closed successfully',
    error: 'Failed to close requisition',
  },
  archive: {
    success: 'Requisition archived successfully',
    error: 'Failed to archive requisition',
  },
  make_confidential: {
    success: 'Requisition marked as confidential',
    error: 'Failed to update requisition',
  },
  make_public: {
    success: 'Requisition marked as public',
    error: 'Failed to update requisition',
  },
  open: {
    success: 'Requisition opened successfully',
    error: 'Failed to open requisition',
  },
  reopen: {
    success: 'Requisition reopened successfully',
    error: 'Failed to reopen requisition',
  },
  clone: {
    success: 'Requisition cloned successfully',
    error: 'Failed to clone requisition',
  },
};

interface UseRequisitionActionsProps {
  onRefetch?: (action: RequisitionActionType) => void;
  onOpenDrawer?: (requisition: Requisition) => void;
  onCloseReqFromMenu?: () => void;
}

export const useRequisitionActions = ({
  onRefetch,
  onOpenDrawer,
  onCloseReqFromMenu = () => {},
}: UseRequisitionActionsProps = {}) => {
  const toast = useShowToast();
  const queryClient = useQueryClient();
  const [, setUrlState] = useUrlState();

  const [modalState, setModalState] = useState<{
    isOpen: boolean;
    selectedRequisition: Requisition | null;
    activeAction: RequisitionActionType | null;
    applyToGroup?: boolean;
    allGroupRequisitions?: Requisition[];
  }>({
    isOpen: false,
    selectedRequisition: null,
    activeAction: null,
    applyToGroup: false,
    allGroupRequisitions: [],
  });

  // Generic mutation handlers - DRY success/error handling
  const onMutationSuccess = useCallback(
    (action: RequisitionActionType) => {
      const messages = MUTATION_MESSAGES[action];
      toast({ title: messages.success, status: 'success' });
      queryClient.invalidateQueries({ queryKey: [API_KEYS.getRequisitions] });
      if (onRefetch) {
        onRefetch?.(action);
      }
    },
    [toast, queryClient] // eslint-disable-line react-hooks/exhaustive-deps
  );

  const onMutationError = useCallback(
    (action: string, error: any) => {
      const messages = MUTATION_MESSAGES[action];
      const errorMessage = getRequisitionErrorMessage(error, false);
      toast({
        title: messages.error,
        description: errorMessage,
        status: 'error',
      });
    },
    [toast]
  );

  // All mutations using the same pattern
  const closeRequisitionMutation = useMutation({
    mutationFn: ({ requisitionId, payload }: any) =>
      API_SERVICE.closeRequisition(requisitionId, payload),
    onSuccess: () => {
      onMutationSuccess('close');
      onCloseReqFromMenu?.();
    },
    onError: (error: any) => onMutationError('close', error),
  });

  const archiveRequisitionMutation = useMutation({
    mutationFn: ({ requisitionId, payload }: any) =>
      API_SERVICE.archiveRequisition(requisitionId, payload),
    onSuccess: () => onMutationSuccess('archive'),
    onError: (error: any) => onMutationError('archive', error),
  });

  const makeConfidentialMutation = useMutation({
    mutationFn: ({ requisitionId, payload }: any) =>
      API_SERVICE.makeRequisitionConfidential(requisitionId, payload),
    onSuccess: () => onMutationSuccess('make_confidential'),
    onError: (error: any) => onMutationError('make_confidential', error),
  });

  const makePublicMutation = useMutation({
    mutationFn: ({ requisitionId, payload }: any) =>
      API_SERVICE.makeRequisitionNonConfidential(requisitionId, payload),
    onSuccess: () => onMutationSuccess('make_public'),
    onError: (error: any) => onMutationError('make_public', error),
  });

  const openRequisitionMutation = useMutation({
    mutationFn: ({ requisitionId, payload }: any) =>
      API_SERVICE.openRequisition(requisitionId, payload),
    onSuccess: () => onMutationSuccess('open'),
    onError: (error: any) => onMutationError('open', error),
  });

  const reopenRequisitionMutation = useMutation({
    mutationFn: ({ requisitionId, payload }: any) =>
      API_SERVICE.reopenRequisition(requisitionId, payload),
    onSuccess: () => onMutationSuccess('reopen'),
    onError: (error: any) => onMutationError('reopen', error),
  });

  const cloneRequisitionMutation = useMutation({
    mutationFn: ({ requisitionId }: { requisitionId: string }) =>
      API_SERVICE.cloneRequisition(requisitionId),
    onSuccess: () => onMutationSuccess('clone'),
    onError: (error: any) => onMutationError('clone', error),
  });

  // Map for easy access - memoized to prevent unnecessary re-renders
  const mutations = useMemo(
    () => ({
      close: closeRequisitionMutation,
      close_single: closeRequisitionMutation,
      archive: archiveRequisitionMutation,
      make_confidential: makeConfidentialMutation,
      make_public: makePublicMutation,
      open: openRequisitionMutation,
      reopen: reopenRequisitionMutation,
      clone: cloneRequisitionMutation,
    }),
    [
      closeRequisitionMutation,
      archiveRequisitionMutation,
      makeConfidentialMutation,
      makePublicMutation,
      openRequisitionMutation,
      reopenRequisitionMutation,
      cloneRequisitionMutation,
    ]
  );

  const openModal = useCallback(
    (
      requisition: Requisition,
      action: RequisitionActionType,
      applyToGroup = false,
      allGroupRequisitions: Requisition[] = []
    ) => {
      setModalState({
        isOpen: true,
        selectedRequisition: requisition,
        activeAction: action,
        applyToGroup,
        allGroupRequisitions,
      });
    },
    []
  );

  const closeModal = useCallback(() => {
    setModalState({
      isOpen: false,
      selectedRequisition: null,
      activeAction: null,
      applyToGroup: false,
      allGroupRequisitions: [],
    });
  }, []);

  // Generic submit handler - reduces 6 handlers to 1 pattern
  const createSubmitHandler = useCallback(
    (action: string, mutation: any, payloadBuilder?: (values: any) => any) => {
      return (values?: any) => {
        setModalState((currentState) => {
          if (!currentState.selectedRequisition) return currentState;

          const params: any = {
            requisitionId: currentState.selectedRequisition.id.toString(),
          };

          if (payloadBuilder && values) {
            params.payload = {
              ...payloadBuilder(values),
              ...(currentState.applyToGroup ? { apply_to_group: true } : {}),
            };
          } else if (currentState.applyToGroup) {
            params.payload = { apply_to_group: true };
          }

          mutation.mutate(params, {
            onSuccess: () => {
              closeModal();
            },
          });

          return currentState;
        });
      };
    },
    [closeModal]
  );

  // Submit handlers using factory
  const submitHandlers = useMemo(
    () => ({
      close: createSubmitHandler(
        'close',
        closeRequisitionMutation,
        (values) => ({
          rejection_reason: values.rejection_reason,
          rejection_reason_description: values.rejection_reason_description,
        })
      ),
      close_single: createSubmitHandler(
        'close_single',
        closeRequisitionMutation,
        (values) => ({
          rejection_reason: values.rejection_reason,
          rejection_reason_description: values.rejection_reason_description,
        })
      ),
      archive: createSubmitHandler('archive', archiveRequisitionMutation),
      make_confidential: createSubmitHandler(
        'make_confidential',
        makeConfidentialMutation
      ),
      make_public: createSubmitHandler('make_public', makePublicMutation),
      open: createSubmitHandler('open', openRequisitionMutation),
      reopen: createSubmitHandler(
        'reopen',
        reopenRequisitionMutation,
        (values) => ({ reopen_reason: values.reopen_reason })
      ),
      clone: () => {
        setModalState((currentState) => {
          if (!currentState.selectedRequisition) return currentState;

          cloneRequisitionMutation.mutate(
            { requisitionId: currentState.selectedRequisition.id.toString() },
            {
              onSuccess: (response) => {
                closeModal();
                // Open the cloned draft for editing
                if (response?.requisition?.id) {
                  setUrlState({ draft: response.requisition.id.toString() });
                }
              },
            }
          );

          return currentState;
        });
      },
    }),
    [
      createSubmitHandler,
      closeRequisitionMutation,
      archiveRequisitionMutation,
      makeConfidentialMutation,
      makePublicMutation,
      openRequisitionMutation,
      reopenRequisitionMutation,
      cloneRequisitionMutation,
      setUrlState,
      closeModal,
    ]
  );

  const handleAction = useCallback(
    (
      action: RequisitionActionType,
      requisition: Requisition,
      onCloseMenu?: () => void,
      applyToGroup = false,
      allGroupRequisitions: Requisition[] = []
    ) => {
      onCloseMenu?.();

      switch (action) {
        case 'edit':
          // Handle group edit
          if (applyToGroup) {
            setUrlState({
              edit: requisition.id.toString(),
              apply_to_group: 'true',
              view: undefined,
            });
          } else if (requisition.status === 'draft') {
            // For draft status, open draft editor
            setUrlState({ draft: requisition.id.toString() });
          } else {
            // Regular edit
            setUrlState({ edit: requisition.id.toString() });
          }
          break;

        case 'clone':
          openModal(requisition, action, applyToGroup, allGroupRequisitions);
          break;

        case 'make_confidential':
        case 'make_public':
          // Auto-apply to group if requisition is part of a group
          openModal(
            requisition,
            action,
            applyToGroup || !!requisition.requisition_group_id,
            allGroupRequisitions
          );
          break;

        // All modal actions - DRY
        case 'close':
        case 'close_single':
        case 'open':
        case 'reopen':
        case 'archive':
          openModal(requisition, action, applyToGroup, allGroupRequisitions);
          break;

        default:
          console.warn(`Unknown action: ${action}`);
      }
    },
    [onOpenDrawer, openModal, setUrlState] // eslint-disable-line react-hooks/exhaustive-deps
  );

  // Single modal render - replaces 6 individual modal renders
  const renderModals = useCallback(() => {
    const {
      isOpen,
      activeAction,
      selectedRequisition,
      applyToGroup,
      allGroupRequisitions,
    } = modalState;

    if (!activeAction || !mutations[activeAction]) return null;

    return (
      <GenericRequisitionModal
        action={activeAction}
        isOpen={isOpen}
        onClose={closeModal}
        onSubmit={submitHandlers[activeAction]}
        isLoading={mutations[activeAction].isPending}
        requisitionCode={selectedRequisition?.code}
        applyToGroup={applyToGroup}
        allGroupRequisitions={allGroupRequisitions}
      />
    );
  }, [modalState, closeModal, submitHandlers, mutations]);

  return {
    handleAction,
    renderModals,
  };
};
